/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import styled from 'styled-components';
import { Routes, Route, Navigate, useParams, useResolvedPath } from 'react-router-dom';

const RouteRedirectWrapper = styled.div`
  flex: 1;
  width: 100%;
  // background: #fff;
`;

/**
 * The pathname this <Routes> is mounted under.
 *
 * A descendant <Routes> matches against the portion of the URL left over by its
 * parent route, so every child path has to be relative to that parent. This
 * returns the parent's matched pathname with the splat ("*") remainder removed,
 * or "/" when we are at the top of the tree.
 */
const useRouteBase = () => {
  // useResolvedPath('.') gives the full matched pathname of the nearest route,
  // splat included, so the splat has to be trimmed back off.
  const { pathname } = useResolvedPath('.');
  const splat = useParams()['*'];
  const base =
    splat && pathname.endsWith(splat)
      ? pathname.slice(0, pathname.length - splat.length)
      : pathname;
  return base.replace(/\/+$/, '') || '/';
};

/**
 * Rewrites an absolute route path (e.g. "/dashboard/data/*") into one relative
 * to `base` (e.g. "data/*"). Paths that are already relative, and anything
 * outside `base`, are left untouched.
 */
const relativizePath = (base, path) => {
  if (typeof path !== 'string' || !path.startsWith('/') || base === '/') return path;
  if (path === base) return '';
  if (path.startsWith(`${base}/`)) return path.slice(base.length + 1);
  return path;
};

/**
 * A route that hosts its own nested <Routes> has to keep matching as the user
 * navigates deeper, which in v6+ means an explicit trailing "*". Route entries
 * carry the `exact` flag from the v5 config to mark themselves as leaves, so
 * anything without it is treated as a prefix route - the v5 default.
 */
const withSplat = (path, exact) => {
  if (typeof path !== 'string' || exact || path.endsWith('*')) return path;
  return `${path.replace(/\/+$/, '')}/*`;
};

const RouteRedirect = ({ routes = [], redirects = [], className }) => {
  const base = useRouteBase();

  const toRoute = (key, path, element) => {
    const relative = relativizePath(base, path);
    // An empty path is the index route for `base`.
    return relative === '' ? (
      <Route key={key} index element={element} />
    ) : (
      <Route key={key} path={relative} element={element} />
    );
  };

  return (
    <RouteRedirectWrapper className={`${className} RouteRedirect`}>
      <Routes>
        {routes.map(({ component: Component, subRoutes, path, exact }, index) => {
          if (subRoutes) {
            return subRoutes.map((subRoute, keyIndex) =>
              toRoute(
                `${index}-sub-${keyIndex}`,
                withSplat(subRoute.path, subRoute.exact),
                <subRoute.component />,
              ),
            );
          }

          // Normalize path to always be an array for consistent handling
          const paths = Array.isArray(path) ? path : [path];

          return paths.map((singlePath, pathIndex) =>
            toRoute(
              `${index}-${pathIndex}`,
              withSplat(singlePath, exact),
              Component ? <Component /> : undefined,
            ),
          );
        })}
        {/* Redirects are matched as written - they already spell out "/*" where
            they mean to catch everything below `from`. `to` stays absolute: it
            is a URL, not a match pattern. */}
        {redirects.map((redirect, index) =>
          toRoute(
            `redirect-${index}`,
            redirect.from,
            <Navigate to={redirect.to} replace />,
          ),
        )}
      </Routes>
    </RouteRedirectWrapper>
  );
};

export default RouteRedirect;
